/**
 * SAAS-USD-BILLING-1 · Phase 3 — Merchant USD-Settlement history.
 *
 * Displays the merchant's USD invoice batches with friendly status
 * labels only. Internal states (AMBIGUOUS / FAILED_TERMINAL /
 * FAILED_RETRYABLE) are collapsed by the server into
 * `Processing` or `Contact support` — this component never sees the
 * raw internal state.
 *
 * Renders nothing when the merchant has no USD settlements at all,
 * so Retail merchants and unconfigured SaaS merchants get a clean UI.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { FileText } from "lucide-react";

const _STATUS_STYLES = {
  Invoiced:          "bg-emerald-50 text-emerald-800 border border-emerald-200",
  Processing:        "bg-slate-50 text-slate-700 border border-slate-200",
  "Contact support": "bg-amber-50 text-amber-800 border border-amber-200",
};

function _fmt(cents) {
  return `US$${(Number(cents || 0) / 100).toFixed(2)}`;
}

function _fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch (_) {
    return iso;
  }
}

export default function UsdSettlementsCard() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/merchant/billing/usd-settlements")
      .then((r) => { if (!cancelled) setRows(r.data?.settlements || []); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, []);

  if (rows === null) return null;
  if (rows.length === 0) return null;

  return (
    <div className="pf-card p-6 mt-6" data-testid="usd-settlements-card">
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-slate-600" />
        <h3 className="text-lg font-semibold text-slate-900">
          USD invoices & settlements
        </h3>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        Separate USD performance-fee invoices — never combined with your CAD subscription.
      </p>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="usd-settlements-table">
          <thead className="text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left py-2 px-3">Invoice date</th>
              <th className="text-left py-2 px-3">Period</th>
              <th className="text-right py-2 px-3">Amount</th>
              <th className="text-left py-2 px-3">Conversions</th>
              <th className="text-left py-2 px-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => (
              <tr key={row.settlement_id} data-testid={`usd-settlement-row-${row.settlement_id}`}>
                <td className="py-2 px-3 text-slate-700">
                  {_fmtDate(row.invoiced_at || row.period_end)}
                </td>
                <td className="py-2 px-3 text-slate-500 text-xs">
                  {row.period_id}
                </td>
                <td className="py-2 px-3 text-right font-semibold text-slate-900">
                  {_fmt(row.invoiced_amount_minor)}
                </td>
                <td className="py-2 px-3 text-slate-500 text-xs">
                  {row.conversion_count}
                </td>
                <td className="py-2 px-3">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                      _STATUS_STYLES[row.status_label] || _STATUS_STYLES.Processing
                    }`}
                    data-testid={`usd-settlement-status-${row.settlement_id}`}
                  >
                    {row.status_label}
                  </span>
                  {row.stripe_invoice_id && (
                    <div className="text-[10px] text-slate-400 mt-1 font-mono">
                      {row.stripe_invoice_id}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
